import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { RiMailLine, RiArrowLeftLine, RiCheckboxCircleLine, RiErrorWarningLine } from 'react-icons/ri';
import authService from '../../services/authService';
// KD3 S2376: timeout/offline/5xx ไม่มี envelope เดิมโชว์ข้อความ axios ภาษาอังกฤษ ("timeout of 30000ms exceeded") → helper กลางแปลเป็นไทย (P-S043-1)
import { getApiErrorMessage } from '../../utils/mapFieldErrors';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  // เอ๊ะ #4: feedback ของการกด "ส่งลิงก์อีกครั้ง" — success panel เดิมไม่มีที่แสดงผลลัพธ์
  const [resendNote, setResendNote] = useState('');

  // BUG-AUTH-001-DBLLOGIN FIX (sibling): `disabled={isSubmitting}` เป็น state — คลิกที่ 2 ใน tick เดียวกัน
  // ยังผ่านเข้ามาได้ก่อน re-render → ส่งอีเมล + สร้าง reset token ซ้ำ · ref flip แบบ sync กันได้จริง
  const inFlightRef = useRef(false);

  // ส่งซ้ำไปที่อีเมลเดิม โดยไม่ต้องย้อน flow ทั้งหมด · คงข้อความตอบกลับแบบไม่ระบุว่าอีเมลมีจริง
  // (BUG-M04/RISK-AUTH-305: หน้านี้ต้องไม่กลายเป็น oracle บอกว่าอีเมลไหนมีในระบบ)
  const handleResend = async () => {
    if (inFlightRef.current) return; // race-loser drop
    setError('');
    setResendNote('');
    inFlightRef.current = true;
    setIsSubmitting(true);
    try {
      await authService.forgotPassword(email);
      setResendNote('ส่งลิงก์ใหม่แล้ว กรุณาตรวจสอบกล่องจดหมายอีกครั้ง');
    } catch (err: any) {
      setError(
        err?.response?.data?.error?.user_message ||
        err?.response?.data?.detail ||
        getApiErrorMessage(err, 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
      );
    } finally {
      inFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  // กลับไปหน้าฟอร์มพร้อมล้างช่องอีเมล — เคสพิมพ์อีเมลผิด
  const handleTryAnotherEmail = () => {
    setIsSuccess(false);
    setEmail('');
    setError('');
    setResendNote('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inFlightRef.current) return; // race-loser drop
    setError('');

    if (!email.trim()) {
      setError('กรุณากรอกอีเมล');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('รูปแบบอีเมลไม่ถูกต้อง');
      return;
    }

    inFlightRef.current = true;
    setIsSubmitting(true);
    try {
      await authService.forgotPassword(email);
      setIsSuccess(true);
    } catch (err: any) {
      // BUG-S661-001 FIX: read structured error wrapper (error.user_message) — was falling back to raw axios message
      const message =
        err?.response?.data?.error?.user_message ||
        err?.response?.data?.detail ||
        getApiErrorMessage(err, 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      setError(message);
    } finally {
      inFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4" aria-label="หน้าลืมรหัสผ่าน">
      <div className="w-full max-w-md">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4 shadow-lg">
            <span className="text-white font-bold text-2xl">K</span>
          </div>
          <h1 className="text-2xl font-bold text-secondary-900">KRP System</h1>
          <p className="text-secondary-500 text-sm mt-1">ระบบจัดการการเช่าอุปกรณ์</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-secondary-100 p-8">
          {isSuccess ? (
            /* Success State */
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <RiCheckboxCircleLine className="w-9 h-9 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-secondary-900 mb-2">ส่งอีเมลสำเร็จ</h2>
              <p className="text-secondary-600 text-sm mb-2">
                หากอีเมล <span className="font-medium text-secondary-800">{email}</span> มีอยู่ในระบบ
              </p>
              <p className="text-secondary-600 text-sm mb-6">
                คุณจะได้รับลิงก์สำหรับตั้งรหัสผ่านใหม่ภายในไม่กี่นาที
                กรุณาตรวจสอบกล่องจดหมายของคุณ (รวมถึงโฟลเดอร์ Spam)
              </p>
              <p className="text-xs text-secondary-400 mb-6">ลิงก์จะหมดอายุภายใน 30 นาที</p>

              {/* เอ๊ะ #4 (PO-approved 2026-08-14): เมลไม่มา/พิมพ์อีเมลผิด เป็นเรื่องปกติของหน้านี้
                  เดิมมีทางออกทางเดียวคือกลับ login แล้ววน flow เข้ามาใหม่ทั้งหมด */}
              <div className="flex flex-col sm:flex-row gap-2 justify-center mb-6">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'กำลังส่ง...' : 'ส่งลิงก์อีกครั้ง'}
                </button>
                <button
                  type="button"
                  onClick={handleTryAnotherEmail}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 hover:bg-secondary-50 rounded-md transition-colors"
                >
                  ลองอีเมลอื่น
                </button>
              </div>

              {resendNote && (
                <p className="text-sm text-green-600 mb-4" role="status" aria-live="polite">{resendNote}</p>
              )}
              {error && (
                <p className="text-sm text-danger-600 mb-4" role="alert">{error}</p>
              )}

              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm"
              >
                <RiArrowLeftLine className="w-4 h-4" />
                กลับหน้าเข้าสู่ระบบ
              </Link>
            </div>
          ) : (
            /* Form State */
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-secondary-900">ลืมรหัสผ่าน?</h2>
                <p className="text-secondary-500 text-sm mt-1">
                  กรอกอีเมลที่ลงทะเบียนไว้ เราจะส่งลิงก์สำหรับตั้งรหัสผ่านใหม่ให้คุณ
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                {error && (
                  <div id="email-error" className="flex items-start gap-3 bg-danger-50 border border-danger-300 text-danger-700 px-4 py-3 rounded-lg" role="alert">
                    <RiErrorWarningLine className="w-5 h-5 mt-0.5 flex-shrink-0 text-danger-500" />
                    <p className="text-sm font-medium">{error}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-secondary-700">
                    อีเมล
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <RiMailLine className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError('');
                      }}
                      disabled={isSubmitting}
                      className="input w-full h-11 pl-10 text-secondary-900"
                      placeholder="กรอกอีเมลของคุณ"
                      autoComplete="email"
                      autoFocus
                      // BUG-A11Y-FORMFIELD-ARIA (incremental · CRIT auth form): mark the email
                      // field invalid + point to the error box so screen-readers associate them (WCAG 3.3.1)
                      aria-invalid={Boolean(error)}
                      aria-describedby={error ? 'email-error' : undefined}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-11 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium transition-colors duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                      กำลังส่ง...
                    </>
                  ) : (
                    <>
                      <RiMailLine className="w-5 h-5 mr-2" />
                      ส่งลิงก์รีเซ็ต
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 hover:underline"
                >
                  <RiArrowLeftLine className="w-4 h-4" />
                  กลับหน้าเข้าสู่ระบบ
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default ForgotPasswordPage;
